import React, { useEffect, /* useState, */ useCallback } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'; //NEW
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import KanbanCard from '../kanbanCard';
import './kanbanBoard.css';

function KanbanBoard({
    itemsByStatus,
    statusDictionary,
    onChangeStatus,
    onChangeOrder,
    onNotFeasible,
    kanbanItemRenderer,
}) {
    const moveCard = useCallback(
        ({
            movedCardIndexInSourceColumn,
            sourceColumnId,
            destinationColumnId,
            movedCardIndexInDestinationColumn,
        }) => {
            const sourceColumnData = itemsByStatus[sourceColumnId];
            const destinationColumnData = itemsByStatus[destinationColumnId];
            const cardToMove = sourceColumnData[movedCardIndexInSourceColumn];
            const newSourceColumnData = sourceColumnData.filter(
                (card) => card.kanbanId !== cardToMove.kanbanId
            );
            const newDestinationCards = Array.from(destinationColumnData);
            const newIndexInDestination = movedCardIndexInDestinationColumn ?? 0;
            newDestinationCards.splice(newIndexInDestination, 0, cardToMove);
            onChangeStatus(cardToMove.kanbanId, sourceColumnId, destinationColumnId);

            onChangeOrder(sourceColumnId, newSourceColumnData);
            onChangeOrder(destinationColumnId, newDestinationCards);
        },
        [itemsByStatus]
    );

    const reorderCard = useCallback(
        ({ columnId, startIndex, finishIndex }) => {
            const sourceColumnData = itemsByStatus[columnId];
            const updatedItems = reorder({
                list: sourceColumnData,
                startIndex,
                finishIndex,
            });
            const updatedSourceColumn = [...updatedItems];
            onChangeOrder(columnId, updatedSourceColumn);
        },
        [itemsByStatus]
    );

    const handleDrop = useCallback(
        ({ source, location }) => {
            const destination = location.current.dropTargets.length;
            if (!destination) {
                return;
            }
            if (source.data.type === 'card') {
                const draggedCardId = source.data.cardId;
                const [, sourceColumnRecord] = location.initial.dropTargets;
                const sourceColumnId = sourceColumnRecord.data.columnId;
                const sourceColumnData = itemsByStatus[sourceColumnId];
                const draggedCardIndex = sourceColumnData.findIndex(
                    (card) => card.kanbanId === draggedCardId
                );

                if (location.current.dropTargets.length === 1) {
                    const [destinationColumnRecord] = location.current.dropTargets;
                    const destinationColumnId = destinationColumnRecord.data.columnId;
                    if (sourceColumnId === destinationColumnId) {
                        const destinationIndex = getReorderDestinationIndex({
                            startIndex: draggedCardIndex,
                            indexOfTarget: sourceColumnData.cards.length - 1,
                            closestEdgeOfTarget: null,
                            axis: 'vertical',
                        });
                        reorderCard({
                            columnId: sourceColumnData.columnId,
                            startIndex: draggedCardIndex,
                            finishIndex: destinationIndex,
                        });
                        return;
                    }
                    const destinationColumnData = itemsByStatus[destinationColumnId];
                    moveCard({
                        movedCardIndexInSourceColumn: draggedCardIndex,
                        sourceColumnId,
                        destinationColumnId,
                        // assume target is bottom of destination column
                        movedCardIndexInDestinationColumn: destinationColumnData.length,
                    });
                    return;
                }

                if (location.current.dropTargets.length === 2) {
                    const [destinationCardRecord, destinationColumnRecord] =
            location.current.dropTargets;
                    const destinationColumnId = destinationColumnRecord.data.columnId;
                    const destinationColumn = itemsByStatus[destinationColumnId];
                    const indexOfTarget = destinationColumn.findIndex(
                        (card) => card.kanbanId === destinationCardRecord.data.cardId
                    );
                    const closestEdgeOfTarget = extractClosestEdge(
                        destinationCardRecord.data
                    );
                    if (sourceColumnId === destinationColumnId) {
                        const destinationIndex = getReorderDestinationIndex({
                            startIndex: draggedCardIndex,
                            indexOfTarget,
                            closestEdgeOfTarget,
                            axis: 'vertical',
                        });
                        reorderCard({
                            columnId: sourceColumnId,
                            startIndex: draggedCardIndex,
                            finishIndex: destinationIndex,
                        });
                    } else {
                        const destinationIndex =
              closestEdgeOfTarget === 'bottom'
                  ? indexOfTarget + 1
                  : indexOfTarget;
                        moveCard({
                            movedCardIndexInSourceColumn: draggedCardIndex,
                            sourceColumnId,
                            destinationColumnId,
                            movedCardIndexInDestinationColumn: destinationIndex,
                        });
                    }
                }
            }
        },
        [itemsByStatus]
    );

    useEffect(() => {
        return monitorForElements({
            onDrop: handleDrop,
        });
    }, [handleDrop]);

    const totalCards = Object.keys(itemsByStatus).length;

    return (
        <div
            className="me-4 mb-5 kanbanBoard"
            style={{ gap: totalCards <= 4 ? '10px' : '6px' }}
        >
            {Object.keys(itemsByStatus).map((status, index) => (
                <KanbanCard
                    key={`kanbancard-${index}`}
                    id={String(status)}
                    title={statusDictionary[status]}
                    items={itemsByStatus[status]}
                    onNotFeasible={onNotFeasible}
                    kanbanItemRenderer={kanbanItemRenderer}
                    totalCards={totalCards}
                />
            ))}
        </div>
    );
}

export default KanbanBoard;
